// types.ts — core type definitions for the BroodGame engine.
//
// RUNTIME NOTE: this file is loaded by Node v22 native type-stripping (no build step).
// That forbids `enum` / `const enum` / `namespace`. We use `as const` objects + union
// literal types instead, which strip to plain object literals at runtime.

/** An entity is an integer index into the SoA component arrays. */
export type EntityId = number;

/** A logical simulation tick (monotonic, integer). */
export type Tick = number;

/** Sentinel for "no entity" / empty list slot in intrusive structures. */
export const NIL: EntityId = -1;

// ---------------------------------------------------------------------------
// Entity kinds
// ---------------------------------------------------------------------------

export const KIND = {
  Empty: 0,
  Ant: 1,
  Food: 2,
  /** A caste-bearing colony member (phase 2). See CASTE + the Organism schema. */
  Organism: 3,
  /** Non-colony wildlife (phase 3 ecosystem layer). See SPECIES + wildlife.ts. */
  Wildlife: 4,
  /** A rare ecosystem BOSS (phase 14 apex layer). See APEX_KIND + apex.ts. */
  Apex: 5,
  /** The colony's persistent queen — an immobile root unit at the nest with its own
   *  HP. See ColonySystem queen-entity (opt-in). NOT a caste; not counted in
   *  population/casteCount; never moved by the organism loop or added to the grid. */
  Queen: 6,
} as const;
export type Kind = (typeof KIND)[keyof typeof KIND];

// ---------------------------------------------------------------------------
// Ant behavioural state (kept tiny + integer so it hashes cleanly)
// ---------------------------------------------------------------------------

export const ANT_STATE = {
  /** Looking for food / wandering. */
  Foraging: 0,
  /** Adjacent to food and consuming. */
  Feeding: 1,
} as const;
export type AntState = (typeof ANT_STATE)[keyof typeof ANT_STATE];

// ---------------------------------------------------------------------------
// Directions — 8-directional (Moore) movement, plus an explicit "stay".
// The order is STABLE and load-bearing: pathing/wander tie-breaking depends on
// iterating these in exactly this order. Index 8 (Stay) is never used for a
// move; it is the canonical "no direction" result.
// ---------------------------------------------------------------------------

export const DIR = {
  N: 0,
  NE: 1,
  E: 2,
  SE: 3,
  S: 4,
  SW: 5,
  W: 6,
  NW: 7,
  Stay: 8,
} as const;
export type Direction = (typeof DIR)[keyof typeof DIR];

/** Number of real movement directions (excludes Stay). */
export const DIR_COUNT = 8;

/** Delta-x for each direction index (parallel to DIR_DY). Stable order. */
export const DIR_DX: readonly number[] = [0, 1, 1, 1, 0, -1, -1, -1, 0];
/** Delta-y for each direction index (parallel to DIR_DX). Stable order. */
export const DIR_DY: readonly number[] = [-1, -1, 0, 1, 1, 1, 0, -1, 0];

// ---------------------------------------------------------------------------
// System interface — the tick contract.
// ---------------------------------------------------------------------------

/**
 * A System mutates world state once per tick, in a fixed registration order.
 * Implementations MUST NOT allocate in `update` (reuse scratch buffers).
 */
export interface System {
  /** Stable name for debugging / ordering sanity. */
  readonly name: string;
  /** Advance this system by exactly one tick. */
  update(tick: Tick): void;
}

// ---------------------------------------------------------------------------
// Pathing interface — naive greedy now, A* later behind the same contract.
// ---------------------------------------------------------------------------

/**
 * Returns a Direction index to move one step from (fromX,fromY) toward
 * (toX,toY), or DIR.Stay if no admissible step makes progress. Must be
 * deterministic (stable tie-breaking) and allocation-free.
 */
export interface Pathing {
  step(fromX: number, fromY: number, toX: number, toY: number): Direction;
}

// ---------------------------------------------------------------------------
// Configuration interfaces
// ---------------------------------------------------------------------------

/** Tuning knobs for the ant foraging system. All integer. */
export interface AntConfig {
  /** Energy lost per tick (Foraging). */
  readonly energyDecay: number;
  /** Energy gained per unit of food consumed. */
  readonly foodEnergy: number;
  /** Starting energy of a freshly spawned ant. */
  readonly startEnergy: number;
  /** Chebyshev radius the ant can "see" food within. */
  readonly senseRadius: number;
}

/** Tuning knobs for food resource nodes. All integer. */
export interface ResourceConfig {
  /** Ticks after depletion before a node regenerates. */
  readonly regenDelay: number;
  /** Amount a node regenerates back to. */
  readonly regenAmount: number;
}

/** Full world configuration for a headless run. */
export interface WorldConfig {
  readonly seed: number;
  readonly width: number;
  readonly height: number;
  readonly ants: number;
  readonly food: number;
  /** Initial amount per food node. */
  readonly foodAmount: number;
  /** Hard cap on ants per tile (collision). Multiple kinds still coexist. */
  readonly maxAntsPerTile: number;
  readonly ant: AntConfig;
  readonly resource: ResourceConfig;
}

// ===========================================================================
// PHASE 2 — caste-based colony layer
// ===========================================================================
//
// "Organism" is NOT a class. In a data-oriented engine the base structure is a
// SHARED COMPONENT SCHEMA, and every caste "inherits" it by reusing the same
// SoA columns on EntitySystem. An organism is the set of columns below, read by
// integer id — never a heavy per-entity object (DESIGN.md §2, EFFICIENCY.md §B):
//
//   kind   = KIND.Organism      caste  = CASTE.*        colony = ColonyId
//   posX/posY   energy   hp   cargo   targetX/targetY   state (ORGANISM_STATE)
//
// A "caste" is likewise data, not a subclass: a stateless BEHAVIOUR TEMPLATE
// (one singleton per caste, shared by thousands of members — see sim/castes.ts)
// plus a CasteConfig of tuning knobs. Behaviour first, stats second.

/** Stable id of a colony (index into the ColonySystem's compact store). */
export type ColonyId = number;

/** The four castes. Values are dense indices into the behaviour/config tables. */
export const CASTE = {
  /** Gather food and return it to the colony. */
  Worker: 0,
  /** Patrol the colony and attack rival organisms. */
  Soldier: 1,
  /** Scout the map; ignores walls and tile crowding (it flies). */
  Flyer: 2,
  /** Dormant until a colony stress trigger fires, then acts. */
  Specialist: 3,
} as const;
export type Caste = (typeof CASTE)[keyof typeof CASTE];

/** Number of castes (size of the caste-indexed lookup tables). */
export const CASTE_COUNT = 4;

/**
 * Per-organism behavioural state. Integer + tiny so it hashes cleanly. States
 * are shared across castes where the meaning lines up (e.g. a triggered
 * Specialist reuses the Worker gather/return states).
 */
export const ORGANISM_STATE = {
  /** Dormant (Specialist before its trigger). Costs ~nothing per tick. */
  Idle: 0,
  /** Outbound, looking for food (Worker / triggered Specialist). */
  SeekingFood: 1,
  /** Carrying cargo home (Worker / triggered Specialist). */
  Returning: 2,
  /** Roaming near home (Soldier with no target). */
  Patrolling: 3,
  /** Closing on / striking an enemy (Soldier). */
  Attacking: 4,
  /** Scouting outward (Flyer). */
  Exploring: 5,
} as const;
export type OrganismState = (typeof ORGANISM_STATE)[keyof typeof ORGANISM_STATE];

/** Tuning knobs for a single caste's behaviour template. All integer. */
export interface CasteConfig {
  /** Energy a freshly spawned member of this caste starts with. */
  readonly startEnergy: number;
  /** Energy lost per tick while active (0 = never starves, e.g. dormant). */
  readonly energyDecay: number;
  /** Starting / max hit points (combat). */
  readonly startHp: number;
  /** Chebyshev radius this caste can sense food / enemies within. */
  readonly senseRadius: number;
  /** Food units a Worker/Specialist carries per trip. */
  readonly carryCapacity: number;
  /** Damage dealt per attack tick (Soldier). */
  readonly attackPower: number;
  /** Roam radius around home (Soldier patrol / Flyer refuel band). */
  readonly roamRadius: number;
}

/** Tuning knobs for a colony (the persistent root object). All integer. */
export interface ColonyConfig {
  /** Food units a colony must hold to spawn one new organism. */
  readonly spawnCost: number;
  /** Ticks between spawns (throttles growth, keeps it deterministic). */
  readonly spawnCooldown: number;
  /** Hard cap on live members per colony. */
  readonly maxPopulation: number;
  /** Food the colony starts with (seed capital for the first spawns). */
  readonly initialFood: number;
  /** Food level below which Specialists wake and forage. */
  readonly lowFoodThreshold: number;
  /**
   * OPT-IN larva life-stage. When > 0, the colony's AUTONOMOUS growth no longer
   * pops out adults instantly: a spawn cycle LAYS a larva that develops for this
   * many ticks before HATCHING into the adult (a real egg→larva→adult pipeline).
   * Founders (`seedInitialMembers`), the lone-queen emergency rescue, and emergency
   * recovery still appear instantly — only the steady reproductive stream incubates.
   * Absent / 0 ⇒ instant spawn, byte-identical to the original behaviour.
   */
  readonly larvaMaturation?: number;
  /**
   * OPT-IN: spawn a REAL immobile queen ENTITY (KIND.Queen) at a PROTECTED colony's
   * nest — her own HP + visual, folded into the hash via the generic entity walk.
   * Absent/false ⇒ no queen entity, byte-identical.
   */
  readonly queenEntity?: boolean;
  /**
   * OPT-IN per-creature MOVEMENT SPEED. When true, organisms move via a fixed-point
   * sub-tick accumulator over each genome's `speed` STAT (sim/genome.ts SPEED_UNIT) —
   * so a slow tank moves sub-tile and a fast scout moves smoothly, instead of the
   * coarse integer `stepsPerTick` (floored at 1 tile/tick). The world builders thread
   * it into the OrganismCtx and fold `moveAccum` into the state hash (guarded).
   * Absent/false ⇒ the original integer stepsPerTick movement, BYTE-IDENTICAL.
   */
  readonly creatureSpeed?: boolean;
}

/**
 * Colony-persistence (queen) rules. A PROTECTED colony — the player's — experiences
 * setbacks but never *accidental* extinction. At/below `criticalPopulation` it enters
 * EMERGENCY RECOVERY: cheap, fast, worker-only spawns (reproduction up, expansion +
 * military paused). With only the queen left (pop 0) she raises FREE emergency larvae
 * on the `emergencyCooldown`, so the colony always claws back from zero. The colony
 * can become weak — it cannot silently disappear. Unprotected colonies are unaffected.
 */
export interface QueenConfig {
  /** Population at/below which a protected colony enters emergency recovery. */
  readonly criticalPopulation: number;
  /** Reduced food cost per recovery spawn (paid down to free when the larder is empty). */
  readonly recoverySpawnCost: number;
  /** Faster spawn cadence (ticks) while recovering. */
  readonly recoveryCooldown: number;
  /** Cadence (ticks) for a lone queen's free emergency larvae. */
  readonly emergencyCooldown: number;
}

// ===========================================================================
// GENE-LOOT SYSTEM — index constants
// ===========================================================================
// Grounded in docs/LORE.md §"Genes — evolution as modular loot".
// All values are dense 0-based indices; array lengths are the COUNT siblings.
// The ordering is a HARD CONTRACT shared by src/sim/geneData.ts and the engine.

/** The six base body-part gene types. */
export const GENE_TYPE = {
  Wing:       0,
  Mandible:   1,
  VenomGland: 2,
  Carapace:   3,
  Antenna:    4,
  Limb:       5,
} as const;
export type GeneType = (typeof GENE_TYPE)[keyof typeof GENE_TYPE];
export const GENE_TYPE_COUNT = 6;

/** The seven stackable mutation traits a gene can carry. */
export const GENE_TRAIT = {
  Lightweight:   0,
  Reinforced:    1,
  Toxic:         2,
  Regenerative:  3,
  Enlarged:      4,
  Acute:         5,
  Volatile:      6,
} as const;
export type GeneTrait = (typeof GENE_TRAIT)[keyof typeof GENE_TRAIT];
export const GENE_TRAIT_COUNT = 7;

/** Five rarity tiers for gene items, Common→Legendary. */
export const RARITY = {
  Common:    0,
  Uncommon:  1,
  Rare:      2,
  Epic:      3,
  Legendary: 4,
} as const;
export type Rarity = (typeof RARITY)[keyof typeof RARITY];
export const RARITY_COUNT = 5;

/**
 * Behaviour/visual channels that genes modulate. These are NOT raw stat
 * columns — they describe which engine channel the gene's effect is routed
 * to (movement rhythm, combat aggression, visual scale, etc.).
 * Size doubles as the visual-scale channel.
 */
export const EFFECT_CHANNEL = {
  Movement:  0,
  Combat:    1,
  Sensing:   2,
  Defense:   3,
  Toxicity:  4,
  Regen:     5,
  Size:      6,
} as const;
export type EffectChannel = (typeof EFFECT_CHANNEL)[keyof typeof EFFECT_CHANNEL];
export const EFFECT_CHANNEL_COUNT = 7;

/** Tuning knobs for the gene-loot system. All integer. */
export interface GeneConfig {
  readonly perColonyCapacity: number;    // max genes a single colony can hold
  readonly combatDropChancePct: number;  // % a slain rival yields a gene to the KILLER's colony
  readonly deathDropChancePct: number;   // % a naturally-dead member salvages a gene to its OWN colony
  /**
   * OPT-IN eviction policy. When a colony is full and a NEW gene would be
   * inserted, if its rarity is strictly higher than the colony's lowest-rarity
   * held gene, evict that lowest (ties → the oldest in list order) to make room;
   * otherwise discard as usual. Absent/false ⇒ today's behaviour (discard the
   * drop, bump droppedFullTotal) byte-identical.
   */
  readonly evictWhenFull?: boolean;
}

/** Full configuration for a headless caste-colony run. */
export interface ColonyWorldConfig {
  readonly seed: number;
  readonly width: number;
  readonly height: number;
  /** Number of colonies to seed. */
  readonly colonies: number;
  /** Organisms each colony starts with (split across castes by the spawn cycle). */
  readonly startMembers: number;
  readonly food: number;
  readonly foodAmount: number;
  /** Hard cap on grounded units (ants/ground organisms) per tile. */
  readonly maxPerTile: number;
  /** Per-caste tuning, indexed by CASTE.*. */
  readonly castes: readonly CasteConfig[];
  readonly colony: ColonyConfig;
  readonly resource: ResourceConfig;
  /** Gene-loot tuning (optional; falls back to a default when omitted). */
  readonly gene?: GeneConfig;
  /** Colony-AI tuning (optional; falls back to a default when omitted). */
  readonly ai?: ColonyAIConfig;
  /** Colony-personality tuning (optional; falls back to a default when omitted). */
  readonly personality?: PersonalityConfig;
  /** Leaderboard tuning (optional; falls back to a default when omitted). */
  readonly leaderboard?: LeaderboardConfig;
  /** Physical-hive tuning (optional; falls back to a default when omitted). */
  readonly hive?: HiveConfig;
  /**
   * Queen-persistence (recovery) tuning (optional; falls back to `DEFAULT_QUEEN_CONFIG`
   * when omitted ⇒ byte-identical). Lets balance work sweep `criticalPopulation`/recovery
   * costs — e.g. via the demo CLI's `--queen-*` flags.
   */
  readonly queen?: QueenConfig;
}

// ===========================================================================
// COLONY-EVOLUTION LEADERBOARD — read-only aggregation layer
// ===========================================================================
// A persistent standings layer that SCORES each colony's evolutionary progress
// across a fixed set of dimensions and ranks them. It OBSERVES the existing
// systems (colony / colony-AI / gene inventory) through their public accessors
// only — it never mutates them — and runs LAST in the tick order, writing only
// its own store. Pure integer state (exact hashing); no RNG (scoring is a pure
// function of world state). See src/sim/leaderboard.ts.

/**
 * Leaderboard score dimensions (dense 0-based indices; COUNT sibling). In order:
 * GenomeComplexity, GeneRarity, TerritoryControl, ColonySize, EcosystemInfluence,
 * RegionDominance, and Discovery — emergent-morph renown contributed by the
 * Evolution-Discovery observer (see src/sim/discovery.ts).
 */
export const SCORE_DIM = {
  GenomeComplexity:   0,
  GeneRarity:         1,
  TerritoryControl:   2,
  ColonySize:         3,
  EcosystemInfluence: 4,
  RegionDominance:    5,
  Discovery:          6,
} as const;
export type ScoreDim = (typeof SCORE_DIM)[keyof typeof SCORE_DIM];
export const SCORE_DIM_COUNT = 7;

/** Tuning for the colony-evolution leaderboard. All integer. */
export interface LeaderboardConfig {
  /** Recompute standings every `cadence` ticks (deterministic). */
  readonly cadence: number;
  /** totalScore = instant + floor(lifetime / lifetimeDivisor). Larger ⇒ progression weighs less. */
  readonly lifetimeDivisor: number;
  /** Top-level weight per SCORE_DIM.* (length SCORE_DIM_COUNT). */
  readonly weights: readonly number[];
}

// ===========================================================================
// COLONY AUTONOMY — high-level decision + task-scheduling constants
// ===========================================================================
// The colony runs ITSELF: each colony weighs a fixed set of NEED signals on a
// stagger cadence, arbitrates a single winning action by fixed priority, and
// drives castes through a pooled TASK queue. Pure integer state (exact hashing),
// no player input. See src/sim/colonyAI.ts (the decision engine) and
// src/sim/taskQueue.ts (the scheduler).

/** A task is an integer id = a row index into the TaskQueue columns. */
export type TaskId = number;

/** Colony need signals the AI weighs each decision. Dense 0-based indices. */
export const NEED = {
  Food: 0,
  Defense: 1,
  Expansion: 2,
  Reproduction: 3,
  Evolution: 4,
} as const;
export type Need = (typeof NEED)[keyof typeof NEED];
/** Number of need channels (size of the per-colony need vector). */
export const NEED_COUNT = 5;

/**
 * Task kinds the scheduler assigns to castes. Dense 0-based indices. The first
 * four are the original autonomy tasks; Nurse/Haul/Repair (added later) wire to
 * the physical hive — a Worker/Specialist tends a Nursery, hauls Storage food to
 * the nest, or repairs a degraded chamber. The ORDER is a HARD CONTRACT (it is
 * the index of every task-kind-keyed table + the hash fold).
 */
export const TASK = {
  Forage: 0,
  Defend: 1,
  Scout: 2,
  Expand: 3,
  /** Tend a Nursery chamber: accelerate the colony's brood (cut spawn cooldown). */
  Nurse: 4,
  /** Carry reserve food from a Storage chamber to the nest (eases a famine). */
  Haul: 5,
  /** Restore a degraded chamber's repair meter back toward full. */
  Repair: 6,
} as const;
export type TaskKind = (typeof TASK)[keyof typeof TASK];
/** Number of task kinds. */
export const TASK_KIND_COUNT = 7;

// ===========================================================================
// COLONY PERSONALITY EVOLUTION — emergent temperament from repeated decisions
// ===========================================================================
// A colony's character is not in its genes but in its CHOICES. The autonomy
// layer (colonyAI.ts) picks one winning NEED each decision cadence; those
// repeated choices (plus a couple of outcome signals — kills, priority switches)
// accumulate into a per-colony TRAIT-tendency vector. Once the dominant tendency
// clears a threshold it CRYSTALLIZES into a named personality, which then applies
// a SMALL, capped bias back onto the AI's need scores — a self-reinforcing loop
// that makes two colonies with identical genes diverge by circumstance. Pure
// integer SoA state (exact hashing); no RNG — personality is a deterministic
// function of decisions. See src/sim/personality.ts.

/** Emergent colony personality traits (dense indices; COUNT sibling). */
export const TRAIT = {
  Aggressive: 0,
  Expansionist: 1,
  Industrial: 2,
  Defensive: 3,
  Adaptive: 4,
  Predatory: 5,
} as const;
export type Trait = (typeof TRAIT)[keyof typeof TRAIT];
/** Number of personality traits (size of the per-colony trait-score vector). */
export const TRAIT_COUNT = 6;
/** Sentinel: no personality has emerged yet (scores below the threshold). */
export const TRAIT_FORMING = -1;

/** Tuning knobs for the colony-personality evolution layer. All integer. */
export interface PersonalityConfig {
  /** Trait points awarded per matching decision (the core accumulation rate). */
  readonly priorityWeight: number;
  /** Adaptive points when the chosen priority changes between decisions. */
  readonly switchWeight: number;
  /** Adaptive points when Evolution is the chosen priority. */
  readonly evolveWeight: number;
  /** Predatory points per rival kill scored since the last decision. */
  readonly killWeight: number;
  /** soldiers*100/pop ≥ this on a Defense decision ⇒ Aggressive, else Defensive. */
  readonly soldierRatioPct: number;
  /** The dominant score must reach this before a personality "emerges". */
  readonly emergeThreshold: number;
  /** Need-score bias applied for an emerged trait (keep small, e.g. 3). */
  readonly biasStrength: number;
  /** Trait points per structural sample from the colony's dominant gene channel
   *  (genes shape identity, not just decisions). 0 ⇒ genes don't influence it. */
  readonly geneWeight: number;
  /** Trait points per held hive node (capped) toward Expansionist each sample —
   *  territory the colony has CLAIMED reads as an expansionist character. */
  readonly territoryWeight: number;
  /**
   * SLOW-DECAY (EMA) divisor (optional). On a fixed cadence each trait score sheds
   * `floor(score / decayDivisor)`, so old signal fades while recent decisions keep
   * weight — a colony's temperament can drift over a long run instead of an early
   * choice crystallizing forever. Larger ⇒ gentler decay (slower drift, higher
   * caps). Omitted ⇒ personality.ts supplies its own default (decay is ON by
   * default). ≤ 0 disables decay (the legacy monotonic-accumulation model). */
  readonly decayDivisor?: number;
}

/** Tuning knobs for the colony-AI decision engine. All integer. */
export interface ColonyAIConfig {
  /** Ticks between a colony's decisions (decisions run on a cadence). */
  readonly decisionInterval: number;
  /** foodStore below this (relative to population) ⇒ a Food need. */
  readonly lowFood: number;
  /** Nest-scan Chebyshev radius for rival organisms (drives Defense). */
  readonly threatRadius: number;
  /** Population per existing hive node before Expansion fires. */
  readonly expandPopThreshold: number;
  /** Hard cap on hive nodes a colony may found. */
  readonly maxHiveNodes: number;
  /** Food granted to a colony when it founds a hive node (new territory). */
  readonly hiveDividend: number;
  /** Genes a colony needs before Evolution fires. */
  readonly evolutionMinGenes: number;
  /** Task-queue capacity per colony. */
  readonly tasksPerColony: number;
  /**
   * Scout-relay timeout (decision cadences). When a colony WANTS to expand but
   * cannot dispatch/complete a Scout for this many consecutive decisions (e.g. it
   * is too crowded-but-poor to spare a Flyer), it founds the hive node DIRECTLY at
   * the nearest passable tile, skipping the Scout step. A deterministic deadlock
   * breaker; the common (Flyer-healthy) path is unaffected because the timer only
   * advances on a decision where Expansion won AND no Scout could be relayed.
   */
  readonly scoutTimeout: number;
  /** Nest food (relative to population) at/below which a colony posts Haul tasks. */
  readonly haulNestThreshold: number;
}

// ===========================================================================
// PHASE 3 — dynamic ecosystem / wildlife layer
// ===========================================================================
//
// Wildlife is NON-COLONY life: it operates independently of colonies, reacts to
// colony expansion (population pressure scales with colony anchors), and feeds on
// insects/food, generating ecological pressure. Like organisms, a wildlife unit
// is NOT a class — it reuses the shared SoA columns (KIND.Wildlife) read by
// integer id: posX/posY, energy, hp, state (WILDLIFE_STATE), targetX/targetY.
// A SPECIES is data: a stateless behaviour template (one singleton per species,
// shared by thousands of units — see sim/wildlife.ts) plus a SpeciesConfig.
//
// The layer is ADDITIVE and self-contained: it depends ONLY on small interfaces
// (ColonyPresence / PreyKillSink / RaidableFood in sim/wildlife.ts), never on the
// colony systems directly, so it runs WITH or WITHOUT colonies.

/** The six wildlife species. Values are dense indices into the behaviour/config tables. */
export const SPECIES = {
  /** Territorial ambush predator: defends a fixed home radius, never roams far. */
  Spider: 0,
  /** Flying migratory hunter: ranges far, ignores walls, hunts any insect. */
  Bird: 1,
  /** Ground omnivore + colony raider: hunts insects, steals food, swarms anchors. */
  Rat: 2,
  /** Small territorial ambush predator (a tighter, weaker Spider). */
  Frog: 3,
  /** Armoured ground scavenger: ignores insects, raids food nodes, drifts near home. */
  Beetle: 4,
  /** Fast, flying TERRITORIAL hunter: darts on insects within its home territory only. */
  Dragonfly: 5,
} as const;
export type Species = (typeof SPECIES)[keyof typeof SPECIES];

/** Number of species (size of the species-indexed lookup tables). */
export const SPECIES_COUNT = 6;

/**
 * Per-wildlife behavioural state. Integer + tiny so it hashes cleanly. Shared
 * across species where the meaning lines up (Roaming/Feeding are universal).
 */
export const WILDLIFE_STATE = {
  /** Drifting near home with no engageable target. */
  Roaming: 0,
  /** Closing on / striking prey (insects). */
  Hunting: 1,
  /** Travelling to a far target (Bird migration / Rat heading to a raid zone). */
  Migrating: 2,
  /** Holding ground against prey inside the territory (Spider / Frog). */
  Defending: 3,
  /** Consuming food (Rat raiding a food node). */
  Feeding: 4,
} as const;
export type WildlifeState = (typeof WILDLIFE_STATE)[keyof typeof WILDLIFE_STATE];

/** Tuning knobs for a single wildlife species' behaviour template. */
export interface SpeciesConfig {
  /** Energy a freshly spawned unit of this species starts with. */
  readonly startEnergy: number;
  /** Energy lost per tick while active. */
  readonly energyDecay: number;
  /** Starting / max hit points (combat). */
  readonly startHp: number;
  /** Chebyshev radius this species can sense prey / food within. */
  readonly senseRadius: number;
  /** Damage dealt per strike on prey. */
  readonly attackPower: number;
  /** Movement steps attempted per tick toward a target (>=1). */
  readonly stepsPerTick: number;
  /** Roam radius around home / a raid anchor (ground drift band). */
  readonly roamRadius: number;
  /** Chebyshev radius an ambush predator defends (won't chase prey beyond it). */
  readonly territoryRadius: number;
  /** Energy gained per successful feed (a kill or a food-node raid). */
  readonly feedEnergy: number;
  /** Ticks between picking a fresh migration target (0 = never, for non-migrants). */
  readonly migrateInterval: number;
  /** Flies: ignores walls + the per-tile cap when moving; doesn't occupy tiles. */
  readonly flying: boolean;
  /** Travels far across the map (Bird) / heads to colony anchors (Rat). */
  readonly migrates: boolean;
  /** Eats colony insects (ants / organisms) on contact. */
  readonly eatsInsects: boolean;
  /** Raids food nodes (KIND.Food) on contact. */
  readonly eatsFood: boolean;
}

/** Tuning knobs for the ecosystem population-dynamics layer. All integer. */
export interface EcosystemConfig {
  /** Ticks between maintenance (spawn) passes. */
  readonly spawnInterval: number;
  /** Carrying capacity floor with zero colonies (keeps wildlife independent). */
  readonly baseCapacity: number;
  /** Extra carrying capacity per colony anchor (pressure scales with expansion). */
  readonly capacityPerAnchor: number;
  /** Hard cap on live wildlife regardless of anchors. */
  readonly maxWildlife: number;
  /** Max wildlife spawned in a single maintenance pass. */
  readonly spawnsPerPass: number;
  /** Initial population per species, indexed by SPECIES.*. */
  readonly initialCounts: readonly number[];
}

/**
 * Ecosystem PRESSURE: how nearby wildlife reshapes colony behaviour. The pressure
 * system senses wildlife around each colony's nest, classifies it (Predation =
 * Spider/Frog, Aerial = Bird/Dragonfly, Theft = Rat), and the colony AI adapts —
 * more soldiers under predators, suppressed flyers under birds, harder foraging
 * when rats rob the larder. All integer + deterministic.
 */
export interface EcosystemPressureConfig {
  /** Ticks between pressure recomputes (a calm cadence — pressure changes slowly). */
  readonly scanInterval: number;
  /** Tiles around the nest within which wildlife is felt as pressure. */
  readonly pressureRadius: number;
  /** Divisor turning raw (proximity-weighted) pressure into a need bias. */
  readonly biasDivisor: number;
  /** Hard cap on any single pressure→need bias (keeps survival dominant). */
  readonly biasCap: number;
  /** Max food a rat presence steals from the larder per scan (scaled by theft). */
  readonly theftDrain: number;
  /** OPTIONAL size-relative theft: per-mille of the colony's STORED food added to the
   *  per-scan steal cap, so a fat larder draws proportionally bigger raids (still bounded
   *  by the sensed theft magnitude + the store). Omitted / 0 ⇒ flat per-rat theft only,
   *  byte-identical to before this field existed. */
  readonly theftLarderPermille?: number;
  /** Predation level at/above which foragers pull in (route planning shrinks). */
  readonly forageSquelch: number;
  /** Aerial level at/above which the colony suppresses Flyer production. */
  readonly aerialSquelch: number;
}

/**
 * Named MAP LANDMARKS that give the world recognisable, influential places. Each
 * type shapes its surroundings differently — see LANDMARK_PROFILE in landmarks.ts.
 */
export const LANDMARK = {
  /** A decaying limb: a little extra forage + light cover. */
  FallenBranch: 0,
  /** Rotting matter: a RICH renewable food hub that draws scavengers (Beetle/Rat). */
  CompostMound: 1,
  /** A nesting site: a persistent BIRD territory (aerial danger). */
  BirdNest: 2,
  /** Fertile soil: a RICH plant-food patch that draws a Beetle. */
  GardenBed: 3,
  /** Heaped stones: an IMPASSABLE cluster (logistics chokepoint) sheltering a Spider. */
  RockCluster: 4,
  /** Standing water: an IMPASSABLE blob (a barrier) ringed by a Frog's territory. */
  WaterPuddle: 5,
} as const;
export type Landmark = (typeof LANDMARK)[keyof typeof LANDMARK];
export const LANDMARK_COUNT = 6;

/** Tuning for the landmark layer (placement + influence magnitudes). */
export interface LandmarkConfig {
  /**
   * Baseline / minimum landmark count. With `densityPer` UNSET this is the exact
   * number placed (legacy behaviour). With `densityPer` set it is the FLOOR of the
   * area-scaled target (so a tiny map still gets at least `count`).
   */
  readonly count: number;
  /** Influence radius (tiles) around each landmark — food spread / territory / barrier size. */
  readonly radius: number;
  /** Ticks between wildlife-replenish passes (keeps a nest/pond alive). */
  readonly respawnInterval: number;
  /**
   * OPT-IN density: roughly one landmark per this many tiles (`width*height`). When
   * set, the effective count scales with map area — clamped to [`count`, `maxCount`]
   * — so a big world gets more landmarks than a small one. ABSENT ⇒ exactly `count`
   * (byte-identical to a pre-density build). Must be a positive integer when set.
   */
  readonly densityPer?: number | undefined;
  /** Upper clamp for the area-scaled count (only consulted when `densityPer` is set). */
  readonly maxCount?: number | undefined;
}

/**
 * Full configuration for a headless ecosystem run. A structural superset of
 * ColonyWorldConfig (re-declared so wildlife.ts/ecosystem.ts need no colony
 * imports) plus the species table and ecosystem tuning.
 */
export interface EcosystemWorldConfig {
  readonly seed: number;
  readonly width: number;
  readonly height: number;
  readonly colonies: number;
  readonly startMembers: number;
  readonly food: number;
  readonly foodAmount: number;
  readonly maxPerTile: number;
  readonly castes: readonly CasteConfig[];
  readonly colony: ColonyConfig;
  readonly resource: ResourceConfig;
  readonly gene?: GeneConfig;
  readonly ai?: ColonyAIConfig;
  readonly leaderboard?: LeaderboardConfig;
  /** Emergent colony-personality tuning (optional — defaults applied if absent).
   *  Present here so the playable ecosystem world grows identities like the CLI
   *  colony world does. */
  readonly personality?: PersonalityConfig | undefined;
  /** Per-species tuning, indexed by SPECIES.*. */
  readonly species: readonly SpeciesConfig[];
  readonly ecosystem: EcosystemConfig;
  /** Dynamic ecosystem events (optional — OFF ⇒ the world is byte-identical). */
  readonly events?: EcosystemEventConfig | undefined;
  /** Apex creatures / bosses (optional — ABSENT ⇒ the world is byte-identical). */
  readonly apex?: ApexWorldConfig | undefined;
  /** Wildlife→colony pressure (optional — ABSENT ⇒ the world is byte-identical). */
  readonly pressure?: EcosystemPressureConfig | undefined;
  /** Evolution-discovery observer (optional — ABSENT ⇒ no system; it's observe-only and
   *  unhashed, so a discovery-less world is byte-identical). */
  readonly discovery?: DiscoveryConfig | undefined;
  /** Named map landmarks (optional — ABSENT ⇒ the world is byte-identical). */
  readonly landmarks?: LandmarkConfig | undefined;
  /**
   * Physical hive — chambers + tunnels + their effects (storage yield, military turret),
   * realised from the AI's founded nodes (optional — ABSENT ⇒ no system, no rng draws, the
   * world is byte-identical to a hive-less build).
   */
  readonly hive?: HiveConfig | undefined;
  /**
   * Layered-timescale ladder (optional — ABSENT ⇒ the world is byte-identical to the
   * pre-timescale build). When set, it overrides the capacity / AI / leaderboard /
   * projects cadences onto the four-layer ladder. Structurally `TimescaleConfig` from
   * src/sim/timescale.ts (kept dependency-light here: { tps; period: number[] }).
   */
  readonly timescale?: { readonly tps: number; readonly period: readonly number[] } | undefined;
}

// ===========================================================================
// PHASE 7b — dynamic ecosystem events
// ===========================================================================
// World-scale disturbances that periodically sweep the ECOSYSTEM world (built by
// buildEcosystemWorld): a pesticide outbreak, a heat wave, a flood, a predator
// migration, a food-surplus bloom. An event scales ecosystem-wide MODIFIERS
// (food regen, wildlife carrying capacity, gene-drop rate, organism drain) for a
// bounded duration and may fire a one-shot onset effect (cull / predator burst /
// food bloom). The layer is OPT-IN (only constructed when EcosystemWorldConfig
// .events is set) and runs on its OWN isolated RNG, so an events-off world — and
// the COLONY world — stay byte-identical. See src/sim/ecosystemEvents.ts.

/** The dynamic ecosystem events (None is the "no event active" sentinel). */
export const ECO_EVENT = {
  None: 0,
  PesticideOutbreak: 1,
  HeatWave: 2,
  Flooding: 3,
  PredatorMigration: 4,
  FoodSurplusBloom: 5,
} as const;
export type EcoEvent = (typeof ECO_EVENT)[keyof typeof ECO_EVENT];

/** Number of REAL events (excludes None) — size of the weight/scheduler tables. */
export const ECO_EVENT_COUNT = 5;

/**
 * One event's effect magnitudes — the persistent modifiers an active event
 * imposes (the first four fields) plus its one-shot ONSET effects (the last
 * three). A table of these is indexed by ECO_EVENT.* (None first), so the order
 * is a HARD CONTRACT. Declared here (dependency-free) so a world can OVERRIDE the
 * shipped magnitudes via EcosystemEventConfig.effects without reaching into the
 * event module; the system's internal EventEffect is structurally identical. See
 * src/sim/ecosystemEvents.ts (EVENT_EFFECTS is the default table).
 */
export interface EcosystemEventEffect {
  /** Resources: regenAmount is scaled by /100 while active (neutral 100). */
  readonly foodRegenPct: number;
  /** Ecosystem: carryingCapacity is scaled by /100 while active (neutral 100). */
  readonly wildlifeCapacityPct: number;
  /** Gene drops: drop chance is scaled by /100 while active (neutral 100). */
  readonly geneDropPct: number;
  /** Organisms: extra energy lost per tick while active (neutral 0). */
  readonly organismDrain: number;
  /** Onset: cull this percent of wildlife (0 = none; flat over total, or per-species
   *  when EcosystemEventConfig.proportionalCull is set). See cullWildlife. */
  readonly cullPct: number;
  /** Onset: spawn N predators (Rat/Bird cycle) (0 = none). */
  readonly predatorBurst: number;
  /** Onset: add N to every on-grid food node (0 = none). */
  readonly bloomAmount: number;
}

/** Tuning knobs for the dynamic-ecosystem-event scheduler. All integer. */
export interface EcosystemEventConfig {
  /** Minimum ticks between events. */
  readonly minGap: number;
  /** + rng.int(gapJitter) → unpredictable spacing between events. */
  readonly gapJitter: number;
  /** Minimum active ticks per event. */
  readonly minDuration: number;
  /** + rng.int(durationJitter) → unpredictable duration. */
  readonly durationJitter: number;
  /** Selection weight per real event (length ECO_EVENT_COUNT, order Pesticide..Bloom). */
  readonly weights: readonly number[];
  /**
   * OPTIONAL per-event effect magnitudes, indexed by ECO_EVENT.* (None first; the
   * order is the same HARD CONTRACT as the default table). When provided it
   * REPLACES the module EVENT_EFFECTS table the system reads; absent/undefined ⇒
   * the system uses the shipped defaults verbatim, so behaviour + hash are
   * byte-identical (the default scheduler never sets this). See ecosystemEvents.ts.
   */
  readonly effects?: readonly EcosystemEventEffect[] | undefined;
  /**
   * OPTIONAL onset-cull mode. When true, a cull event kills a SPECIES-PROPORTIONAL
   * share — floor(speciesCount * cullPct / 100) of EACH species (a representative
   * cross-section). Absent/false ⇒ today's behaviour: the first N wildlife in
   * active-set order (N = floor(totalPopulation * cullPct / 100)), which is
   * byte-identical to a pre-change build (the default scheduler never sets this).
   * See cullWildlife in ecosystemEvents.ts.
   */
  readonly proportionalCull?: boolean | undefined;
}

// ===========================================================================
// EVOLUTION BOXES — additive loot/economy layer (sits ON TOP of gene-loot)
// ===========================================================================
// Each COLONY (the Brood) is the autonomous "player": it earns mutation energy
// (from kills + exploration income), spends it to OPEN tiered loot boxes that
// FORGE genes through the existing forge, and auto-SACRIFICES duplicate genes
// back into energy. Pure integer state (exact hashing). The layer talks to the
// rest of the engine through two tiny zero-dep seams (MutationEnergySink, fed by
// castes.ts on a kill; ColonyEnergySource, read for exploration income), so it
// runs WITH or WITHOUT the colony AI wired. See src/sim/evolutionBox.ts.

/** Loot-box quality tiers, ascending. Dense indices into the per-tier tables. */
export const BOX_TIER = {
  /** Entry tier: cheap, one guaranteed gene, modest rare odds. */
  Common: 0,
  /** Mid tier: pricier, more guaranteed genes, better rare odds. */
  Apex: 1,
  /** Top tier: the colony never advances past it (terminal). */
  Miracle: 2,
} as const;
export type BoxTier = (typeof BOX_TIER)[keyof typeof BOX_TIER];

/** Number of box tiers (size of the per-tier lookup tables). */
export const BOX_TIER_COUNT = 3;

/**
 * Zero-dependency seam: a sink that accrues mutation energy when a colony scores
 * a kill. castes.ts (killOrganism) and the box system share ONLY this interface,
 * so neither depends on the other's concrete type. With no sink wired into a
 * world, kill handling is byte-identical to today.
 */
export interface MutationEnergySink {
  /** Award kill energy to `colony` (the KILLER's colony id). */
  onKill(colony: ColonyId): void;
}

/** Tuning knobs for the Evolution-Box loot/economy layer. All integer. */
export interface EvolutionBoxConfig {
  /** Mutation energy a colony earns per kill it scores. */
  readonly energyPerKill: number;
  /** Exploration income earned per live Flyer, per income pass. */
  readonly exploreEnergyPerFlyer: number;
  /** Ticks between exploration-income passes. */
  readonly incomeInterval: number;
  /** Ticks between auto-sacrifice passes (0 = sacrifice disabled). */
  readonly sacrificeInterval: number;
  /** Copies of a (type,rarity) group kept before extras count as duplicates. */
  readonly keepPerGroup: number;
  /** Opens-since-a-rare that FORCES a guaranteed rare on the next open (pity). */
  readonly pityThreshold: number;
  /** RARITY value at/above which a gene counts as "rare" for pity/streak logic. */
  readonly rareRarityFloor: number;
  /** Forge qualityBonus used when minting the rare bonus gene. */
  readonly rareQualityBonus: number;
  /** Energy a box costs to open, per tier (ascending; indexed by BOX_TIER.*). */
  readonly boxCost: readonly number[];
  /** Genes an open guarantees, per tier (>= 1; indexed by BOX_TIER.*). */
  readonly guaranteedGenes: readonly number[];
  /** Forge qualityBonus for the guaranteed genes, per tier (indexed by BOX_TIER.*). */
  readonly openQualityBonus: readonly number[];
  /** Percent chance (0..100) of a rare BONUS gene, per tier (indexed by BOX_TIER.*). */
  readonly rareChancePct: readonly number[];
  /** Boxes opened AT a tier before advancing to the next (last tier never advances). */
  readonly tierUpAfter: readonly number[];
  /** Energy refunded per sacrificed duplicate, indexed by RARITY.* (Common→Legendary). */
  readonly sacrificeYield: readonly number[];
}

// ===========================================================================
// EVOLUTION DISCOVERY — additive OBSERVER layer (emergent "morph" species)
// ===========================================================================
// A read-only observer that watches each colony's gene pool and AUTO-DISCOVERS
// emergent "morph" species the moment a colony's pool satisfies a hidden synergy
// (a set of required gene types + traits — see src/sim/discoveryData.ts). It is
// permanent-memory: once a morph is discovered it is forever known (status flips
// Undiscovered → Active), and if a colony later stops expressing it the morph
// goes Extinct (and may be Revived). Discovery is a PURE FUNCTION of gene-pool
// state — NO RNG — so same seed + ticks ⇒ identical archives. The layer emits
// DATA-only discovery EVENTS (a fixed-size ring: the headless "animation" seam)
// and contributes per-colony renown to the leaderboard via DiscoveryRenownSource.
// See src/sim/discovery.ts (the system) and src/sim/discoveryExample.ts (world).

/** Lifecycle of a discoverable morph species. Dense 0-based indices. */
export const DISCOVERY_STATUS = {
  /** Never expressed by any colony (its recipe stays concealed). */
  Undiscovered: 0,
  /** Currently expressed by ≥1 colony. */
  Active: 1,
  /** Was discovered, but no colony expresses it any longer (revivable). */
  Extinct: 2,
} as const;
export type DiscoveryStatus = (typeof DISCOVERY_STATUS)[keyof typeof DISCOVERY_STATUS];
/** Number of discovery-status values. */
export const DISCOVERY_STATUS_COUNT = 3;

/** Kind of a discovery event (the data emitted onto the animation-seam ring). */
export const DISCOVERY_EVENT = {
  /** First-ever discovery of a morph (Undiscovered → Active). */
  Discovered: 0,
  /** A morph lost its last expresser (Active → Extinct). */
  Extinct: 1,
  /** An extinct morph regained an expresser (Extinct → Active). */
  Revived: 2,
} as const;
export type DiscoveryEventKind = (typeof DISCOVERY_EVENT)[keyof typeof DISCOVERY_EVENT];
/** Number of discovery-event kinds. */
export const DISCOVERY_EVENT_COUNT = 3;

/** Tuning knobs for the Evolution-Discovery observer layer. All integer. */
export interface DiscoveryConfig {
  /** Re-scan the gene pools every `cadence` ticks (deterministic). */
  readonly cadence: number;
  /** A colony must hold at least this many genes before it expresses any morph. */
  readonly minPoolToExpress: number;
  /**
   * Sticky one-time renown awarded to the DISCOVERER when a morph is first found,
   * indexed by the morph's tier (RARITY.*; length RARITY_COUNT).
   */
  readonly discoveryRenownByTier: readonly number[];
  /**
   * Ongoing renown a colony earns per morph it currently expresses, indexed by
   * the morph's tier (RARITY.*; length RARITY_COUNT). Recomputed each scan.
   */
  readonly expressRenownByTier: readonly number[];
  /** Fixed capacity of the discovery-event ring (the animation seam; > 0). */
  readonly eventRingCapacity: number;
  /** Base animation duration emitted with every event. */
  readonly animBaseDuration: number;
  /** Extra animation duration per morph tier (duration = base + tier*perTier). */
  readonly animDurationPerTier: number;
}

/**
 * Zero-dependency seam the leaderboard reads to fold Discovery renown into a
 * colony's score. The DiscoverySystem implements it; with no source wired the
 * leaderboard's Discovery dimension is simply 0 (existing scores unchanged).
 */
export interface DiscoveryRenownSource {
  /** Total discovery-derived renown for `cid` (sticky discoverer + live express). */
  renownAt(cid: ColonyId): number;
}

// ===========================================================================
// CREATURE SPRITE-ASSEMBLY — index constants for the layered visual SPEC
// ===========================================================================
// The engine is headless: it never rasterizes pixels. It assembles a compact,
// data-oriented SPRITE SPEC — a stack of body PARTS plus optional ATTACHMENTS,
// each with integer scale/colour/variant — that a real 2D renderer would draw
// back-to-front. The spec is derived deterministically from the SAME
// EFFECT_CHANNEL vector that genes/genome already produce (Movement/Combat/…/
// Size), so "evolution changes the look" falls straight out of the gene seam.
// Pure integer state (exact hashing); no RNG. See src/sim/creatureSprite.ts.

/** The four stacked body parts, drawn back→front. Dense 0-based indices. */
export const BODY_PART = {
  /** Rearmost segment; tracks overall size + carries the venom sac. */
  Abdomen: 0,
  /** Mid segment; carries armour plates + the wings. */
  Thorax: 1,
  /** Front segment; carries mandibles + antennae, grows with Combat. */
  Head: 2,
  /** The legs layer; lengthens with Movement. */
  Limbs: 3,
} as const;
export type BodyPart = (typeof BODY_PART)[keyof typeof BODY_PART];

/** Number of body parts (size of the part-indexed sprite columns). */
export const BODY_PART_COUNT = 4;

/**
 * Back→front draw (z) order the renderer composites in. Load-bearing: limbs sit
 * behind the body, the head occludes the thorax. Length == BODY_PART_COUNT.
 */
export const BODY_PART_DRAW_ORDER: readonly BodyPart[] = [
  BODY_PART.Abdomen,
  BODY_PART.Limbs,
  BODY_PART.Thorax,
  BODY_PART.Head,
];

/** Optional attachments layered onto the body. Dense 0-based indices. */
export const ATTACHMENT = {
  /** Movement / Flyer → a pair of wings on the thorax. */
  Wings: 0,
  /** Defense → carapace plates on the thorax (stacks ⇒ more plates). */
  Armor: 1,
  /** Toxicity → a venom sac on the abdomen. */
  VenomSac: 2,
  /** Combat → a pair of mandibles on the head. */
  Mandibles: 3,
  /** Sensing → antennae on the head. */
  Antennae: 4,
} as const;
export type Attachment = (typeof ATTACHMENT)[keyof typeof ATTACHMENT];

/** Number of attachment kinds (size of the attachment-indexed sprite columns). */
export const ATTACHMENT_COUNT = 5;

/**
 * Size of the abstract colour palette a sprite indexes into. The engine only
 * emits palette INDICES in [0, SPRITE_PALETTE_SIZE); the real renderer owns the
 * concrete RGB table. Keeping it integer keeps sprites hashable + deterministic.
 */
export const SPRITE_PALETTE_SIZE = 16;

// ===========================================================================
// PHASE 5 — physical hive / nest expansion
// ===========================================================================
// The lightweight AI "hive nodes" (phase 4) become REAL on-map structures:
// typed chambers (rooms) connected by tunnels, claiming a per-tile territory
// map. Expansion is AUTONOMOUS (driven by the AI's nodes + colony state); there
// is NO player placement. Evolution (genes held) drives structural upgrades.
// Pure integer SoA state (exact hashing). See src/sim/hive.ts.

/** Hive chamber (room) types. */
export const CHAMBER = { Nursery: 0, Storage: 1, Military: 2, Mutation: 3 } as const;
export type ChamberType = (typeof CHAMBER)[keyof typeof CHAMBER];
export const CHAMBER_COUNT = 4;

/** Tuning knobs for the physical hive / expansion system. All integer. */
export interface HiveConfig {
  /** Base room half-extent at level 1. */
  readonly roomRadius: number;
  /** Hard cap on chambers a single colony may build. */
  readonly maxChambersPerColony: number;
  /** Tunnels set walls passable as they dig. */
  readonly tunnelCarves: boolean;
  /** Genes per structural level (evolution upgrades chambers). */
  readonly upgradeGeneThreshold: number;
  /** Hard cap on a chamber's structural level. */
  readonly maxChamberLevel: number;
  /** Food a Nursery grants per effectInterval (×level). */
  readonly nurseryDividend: number;
  /** Ticks between a Mutation room's auto-splice. */
  readonly mutationInterval: number;
  /** Ticks between periodic chamber effects. */
  readonly effectInterval: number;
  /** Storage: food a Storage chamber yields into the colony per cadence (× level). */
  readonly storageYield: number;
  /** Storage: a colony whose Food need is ≥ this stockpiles (builds a Storage room at its nest). */
  readonly storageUrgency: number;
  /** Military: a colony whose Defense need is ≥ this fortifies (builds a Military room). */
  readonly militaryUrgency: number;
  /** Military: hp damage a chamber deals to each rival in range per cadence (× level). */
  readonly militaryDamage: number;
  /** Military: Chebyshev radius a Military chamber strikes rivals within. */
  readonly militaryRadius: number;
  /**
   * Military: Chebyshev radius of the cheap LIVE threat scan at chamber-build time.
   * A realised node sitting within this radius of a hostile (rival organism or
   * wildlife) is built as a Military war-room even when the cadence-stale Defense
   * need has not yet caught up to a threat that arrived between AI decision ticks.
   */
  readonly liveThreatRadius: number;
  /**
   * Full value of a chamber's REPAIR METER (its structural integrity). A fresh /
   * fully-repaired chamber sits at this; wear (chamberDecay) bleeds it down and a
   * Repair task tops it back up. This is the MINIMAL honest damage model: chambers
   * gain a tiny integrity meter rather than a whole HP/siege subsystem.
   */
  readonly repairMax: number;
  /**
   * Integrity a non-core chamber loses per effect cadence (wear). Only chambers
   * BEYOND the Nursery core decay (the core never wears — a colony is never left
   * unable to act), so a colony that expands must eventually maintain its rooms.
   * 0 ⇒ no decay (chambers never degrade; Repair would never trigger).
   */
  readonly chamberDecay: number;
  /** Integrity a single Repair task restores to a degraded chamber. */
  readonly repairAmount: number;
  /** Repair-meter level at/below which a chamber counts as DEGRADED (drives the Repair need + task). */
  readonly repairUrgency: number;
  /** Spawn-cooldown ticks a Nurse task shaves off the colony's next brood (brood acceleration). */
  readonly nurseSpawnBoost: number;
  /** Food a single Haul task moves from a Storage chamber into the colony store. */
  readonly haulAmount: number;
}

// ===========================================================================
// PHASE 14 — APEX CREATURES (rare ecosystem bosses)
// ===========================================================================
// Apex creatures are RARE, single-at-a-time ecosystem BOSSES that live on the
// wildlife/ecosystem layer (KIND.Apex). They are NOT colony members and NOT
// ordinary wildlife: each has its own combat path (it hunts colony organisms,
// disrupts the ecosystem) and its OWN defeat economy (colonies whittle it down
// with Soldiers; the colony that dealt the most damage loots a unique gene).
// A boss does not starve — it escapes after maxLifespan ticks if undefeated.
//
// Like every other entity, an apex is a data-oriented integer-id row reusing the
// shared SoA columns (pos/hp/state/target). The layer is ADDITIVE and OPT-IN:
// EcosystemWorldConfig.apex is undefined ⇒ the world is byte-identical to a
// pre-apex ecosystem (no system constructed, hash fold skipped). See sim/apex.ts.

/**
 * The four apex creatures. Values are dense indices into the per-kind config
 * table. Each maps in flavour to a wildlife species: a giant of its kind.
 */
export const APEX_KIND = {
  /** Huntsman — a colossal ground SPIDER: relentless organism hunter. */
  Huntsman: 0,
  /** Crow Matriarch — an apex flying BIRD: ranges the map, ignores walls. */
  CrowMatriarch: 1,
  /** Rat King — an apex ground RAT: hunts AND summons rat reinforcements. */
  RatKing: 2,
  /** Pond Leviathan — an apex FROG: slow, enormously tanky, devours wildlife. */
  PondLeviathan: 3,
} as const;
export type ApexCreature = (typeof APEX_KIND)[keyof typeof APEX_KIND];

/** Number of apex kinds (size of the per-kind config table). */
export const APEX_KIND_COUNT = 4;

/**
 * Apex BEHAVIOUR mode — the single-boss state machine (territory + mode model).
 * An apex is an ECOSYSTEM FORCE, not a colony assassin: it holds a TERRITORY, the
 * range it patrols, hunts only significant actors near it, RETREATS after a kill,
 * and MIGRATES on a slow cadence (or is forced off a nest it has camped). Dense
 * 0-based indices; folded into the apex hash.
 */
export const APEX_MODE = {
  /** Default: roam within the territory, opportunistically hunt wildlife. */
  Patrol: 0,
  /** Closing on / striking an eligible target. */
  Hunt: 1,
  /** Post-kill withdrawal toward the territory centre (ignores prey). */
  Retreat: 2,
  /** Travelling to a freshly picked territory centre (voluntary or forced). */
  Migrate: 3,
} as const;
export type ApexMode = (typeof APEX_MODE)[keyof typeof APEX_MODE];

/** Per-boss tuning (one entry per APEX_KIND.*). All integer except `flying`. */
export interface ApexConfig {
  /** Starting / max hit points — deliberately MASSIVE (boss health pool). */
  readonly maxHp: number;
  /** Damage the boss deals per strike to a colony organism. */
  readonly attackPower: number;
  /** Chebyshev radius the boss senses prey / wildlife within. */
  readonly senseRadius: number;
  /** Movement steps attempted per tick toward a target (>= 1). */
  readonly stepsPerTick: number;
  /** Flies: ignores walls + the per-tile cap; doesn't occupy a tile. */
  readonly flying: boolean;
  /** Chebyshev radius within which colony Soldiers can damage the boss. */
  readonly vulnRadius: number;
  /** Forge qualityBonus for the unique gene the victor colony loots. */
  readonly geneQualityBonus: number;
  /** Ticks between reinforcement summons (Rat King); 0 = no summons. */
  readonly summonInterval: number;
  /** Ticks the boss survives before it ESCAPES if undefeated. */
  readonly maxLifespan: number;
}

/** Tuning knobs for the apex (boss) layer. All integer except `configs`. */
export interface ApexWorldConfig {
  /** Ticks between spawn-eligibility checks (a boss can only spawn on a check). */
  readonly spawnCheckInterval: number;
  /** Percent chance (0..100) per check that a boss spawns — keep RARE. */
  readonly spawnChancePct: number;
  /** Hp a single adjacent/in-range Soldier strips from the boss per tick. */
  readonly soldierDamage: number;
  /** Defeat-history ring-buffer capacity (oldest milestones overwritten). */
  readonly historyCap: number;
  /** Alert ring-buffer capacity (oldest sightings overwritten). */
  readonly alertCap: number;
  // --- Territory + mode state machine (the ecosystem-force model) ---
  /**
   * Chebyshev radius of the boss's TERRITORY around its centre. It patrols inside
   * this band and will not chase a colony organism beyond it (so it does not bee-
   * line across the map / into a far nest).
   */
  readonly territoryRadius: number;
  /**
   * Ticks between VOLUNTARY migrations (a slow, world-shaping cadence on the order
   * of the Evolution/Civilization timescale — the boss reshapes a new area).
   */
  readonly migrateInterval: number;
  /** Ticks the boss WITHDRAWS toward its territory after a successful kill (anti chain-wipe). */
  readonly retreatTicks: number;
  /**
   * Chebyshev distance to a nest that counts as "near a colony spawn point". Used
   * for anti-camp accounting, the territory HARD-reject buffer, and the no-step-
   * onto-a-nest guard while avoiding nests. ≈ a boss senseRadius + a few.
   */
  readonly nestBuffer: number;
  /**
   * Consecutive ticks within `nestBuffer` of a nest before a FORCED migration —
   * the hard guarantee an apex cannot camp a player nest indefinitely (modest, so
   * camping breaks within ~a minute of sim time).
   */
  readonly maxNestProximityTicks: number;
  /** After a forced (anti-camp) migration, ticks the boss AVOIDS hunting toward / stepping onto nests. */
  readonly nestAvoidTicks: number;
  /**
   * Minimum colony population for that colony's organisms to be HUNTABLE — a
   * fledgling colony is beneath the apex's notice; an established one is prey.
   */
  readonly significancePop: number;
  /** Candidate tiles sampled (deterministic RNG) when picking a new territory. */
  readonly territorySamples: number;
  /** Chebyshev radius of the bounded box scan that SCORES a territory candidate. */
  readonly scoreRadius: number;
  /** Territory score weight per nearby Food node (rich food zones attract). */
  readonly foodWeight: number;
  /** Territory score weight per nearby Wildlife unit (prey concentrations attract). */
  readonly wildlifeWeight: number;
  /** Territory score weight for a CONTESTED signal (≥2 colonies' organisms, or >1 nest nearby). */
  readonly contestedWeight: number;
  /** Territory score PENALTY per (nestBuffer+1 − dist) of proximity to the nearest nest. */
  readonly nestPenalty: number;
  /** Per-kind tuning, indexed by APEX_KIND.* (length APEX_KIND_COUNT). */
  readonly configs: readonly ApexConfig[];
}

/** Apex defeat-history outcome codes (dense; folded into the hash). */
export const APEX_OUTCOME = {
  /** The boss was beaten down by colonies. */
  Defeated: 0,
  /** The boss survived maxLifespan and escaped undefeated. */
  Escaped: 1,
} as const;
export type ApexOutcome = (typeof APEX_OUTCOME)[keyof typeof APEX_OUTCOME];

// ===========================================================================
// TERRITORIAL ESTABLISHMENT — staged region establishment (phase 17)
// ===========================================================================
// New territory is not instantly useful. A region the frontier reaches is only
// EXPLORED; the colony must invest WORKER LABOR to march it through five stages
// to a fully Integrated province. Benefits unlock gradually per stage. Each
// stage is gated on a DIFFERENT one of the four labors (Trail→Logistics→
// FoodTransport→RouteReinforce), so all four are mandatory across the arc.
// Pure integer state (exact hashing). See src/sim/territory.ts.

/** Establishment stages a region climbs (dense 0-based; Unexplored is the locked sentinel). */
export const TERRITORY_STAGE = {
  Unexplored: 0,   // not yet reached by the frontier (region still locked)
  Explored: 1,     // frontier reached — scouted, not yet useful
  Claimed: 2,      // first foothold — trails marked
  Settled: 3,      // logistics established — partial yield
  Established: 4,  // routes carry food home — full yield
  Integrated: 5,   // fully absorbed province — yield + bonus
} as const;
export type TerritoryStage = (typeof TERRITORY_STAGE)[keyof typeof TERRITORY_STAGE];
export const TERRITORY_STAGE_COUNT = 6;
export const TERRITORY_STAGE_NAME = [
  'Unexplored', 'Explored', 'Claimed', 'Settled', 'Established', 'Integrated',
] as const;

/** The four worker labors that establish territory (dense 0-based). */
export const ESTABLISH_WORK = {
  Trail: 0,           // create trails
  Logistics: 1,       // establish logistics
  FoodTransport: 2,   // transport food
  RouteReinforce: 3,  // reinforce routes
} as const;
export type EstablishWork = (typeof ESTABLISH_WORK)[keyof typeof ESTABLISH_WORK];
export const ESTABLISH_WORK_COUNT = 4;
export const ESTABLISH_WORK_NAME = ['Trail', 'Logistics', 'FoodTransport', 'RouteReinforce'] as const;
